import { useEffect, useMemo, useRef, useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import { appearanceColorFields } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/ThemesConfiguration/AppearanceTab/constants'
import {
  getColors,
  getEmailThemePayload,
  getTextStyles,
  parseEmailThemeData,
} from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/ThemesConfiguration/helpers'
import {
  linkStyleDefaults,
  textStyleFields,
} from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/ThemesConfiguration/TextStylesTab/constants'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getConfigurationMode, getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import configurationServices from '@/configuration/services/services'

import { servicesByConfigurationMode } from './constants'
import { getInitialColors, getInitialTextStyles } from './helpers'

const useThemesConfiguration = ({
  merchantId,
  returnPortalId,
  onSaveSettingsType,
  settingsTypeHasChanges,
  isUnifiedDashboard,
  setToastInfo,
  t,
  settingsType,
  revertSettingsType,
}) => {
  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })

  const { fetchData: editEmailTheme, loading: savingEmailTheme } = useFetch(services.editEmailTheme, {
    doInitialRequest: false,
  })

  const {
    data: merchantEmailTheme,
    loading: loadingMerchantEmailTheme,
    fetchData: refetchMerchantEmailTheme,
  } = useFetch(() => configurationServices.getMerchantEmailTheme({ merchantId }), {
    doInitialRequest: configurationMode === configurationTypes.merchant,
  })

  const {
    data: returnPortalEmailTheme,
    loading: loadingReturnPortalEmailTheme,
    fetchData: refetchReturnPortalEmailTheme,
  } = useFetch(() => configurationServices.getPortalEmailTheme({ returnPortalId }), {
    doInitialRequest: configurationMode === configurationTypes.returnsPortal,
  })

  const [currentMode, setCurrentMode] = useState(configurationMode)

  useEffect(() => {
    if (currentMode && configurationMode !== currentMode) {
      if (configurationMode === configurationTypes.merchant) {
        refetchMerchantEmailTheme()
      } else {
        refetchReturnPortalEmailTheme()
      }
    }
    setCurrentMode(configurationMode)
  }, [configurationMode, currentMode, refetchMerchantEmailTheme, refetchReturnPortalEmailTheme])

  const emailThemeData = configurationMode === configurationTypes.merchant ? merchantEmailTheme : returnPortalEmailTheme

  const initialColors = useMemo(() => getInitialColors(appearanceColorFields), [])
  const initialTextStyles = useMemo(() => getInitialTextStyles(textStyleFields), [])

  const [colors, setColors] = useState(initialColors)
  const [footerImage, setFooterImage] = useState(null)
  const [footerImageLink, setFooterImageLink] = useState('')
  const [textStyles, setTextStyles] = useState(initialTextStyles)
  const [linkColor, setLinkColor] = useState(linkStyleDefaults.defaultColor)
  const [hasChanges, setHasChanges] = useState(false)
  const savedState = useRef(null)

  useEffect(() => {
    if (emailThemeData?.background_color !== undefined) {
      const parsed = parseEmailThemeData(emailThemeData)
      setColors(getColors(parsed.colors, initialColors))
      setFooterImage(parsed.footerImage)
      setTextStyles(getTextStyles(parsed.textStyles, initialTextStyles))
      setLinkColor(parsed.linkColor ?? linkStyleDefaults.defaultColor)
      savedState.current = parsed
      setHasChanges(false)
    }
  }, [emailThemeData, initialColors, initialTextStyles])

  const handleColorChange = (key, color) => {
    setColors(prev => ({ ...prev, [key]: color }))
    setHasChanges(true)
  }

  const handleFooterImageChange = image => {
    setFooterImage(image)
    setHasChanges(true)
  }

  const handleFooterImageLinkChange = event => {
    setFooterImageLink(event.target.value)
    setHasChanges(true)
  }

  const handleFontFamilyChange = (key, value) => {
    setTextStyles(prev => ({
      ...prev,
      [key]: { ...prev[key], fontFamily: value },
    }))
    setHasChanges(true)
  }

  const handleFontSizeChange = (key, value) => {
    setTextStyles(prev => ({
      ...prev,
      [key]: { ...prev[key], fontSize: value },
    }))
    setHasChanges(true)
  }

  const handleBoldToggle = key => {
    setTextStyles(prev => ({
      ...prev,
      [key]: { ...prev[key], bold: !prev[key].bold },
    }))
    setHasChanges(true)
  }

  const handleItalicToggle = key => {
    setTextStyles(prev => ({
      ...prev,
      [key]: { ...prev[key], italic: !prev[key].italic },
    }))
    setHasChanges(true)
  }

  const handleTextColorChange = (key, color) => {
    setTextStyles(prev => ({
      ...prev,
      [key]: { ...prev[key], color },
    }))
    setHasChanges(true)
  }

  const handleLinkColorChange = color => {
    setLinkColor(color)
    setHasChanges(true)
  }

  const handleOnSave = async () => {
    try {
      if (!isUnifiedDashboard && settingsTypeHasChanges) {
        await onSaveSettingsType()
      }

      const payload = getEmailThemePayload({
        colors: getColors(colors, initialColors),
        footerImage: footerImage instanceof File ? footerImage : null,
        textStyles: getTextStyles(textStyles, initialTextStyles),
        linkColor: linkColor ?? linkStyleDefaults.defaultColor,
      })

      await editEmailTheme({ ...entityId, payload })

      if (configurationMode === configurationTypes.merchant) {
        await refetchMerchantEmailTheme()
      } else {
        await refetchReturnPortalEmailTheme()
      }

      savedState.current = { colors, footerImage, textStyles, linkColor }
      setHasChanges(false)
      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    if (savedState.current) {
      setColors(getColors(savedState.current.colors, initialColors))
      setFooterImage(savedState.current.footerImage)
      setTextStyles(getTextStyles(savedState.current.textStyles, initialTextStyles))
      setLinkColor(savedState.current.linkColor ?? linkStyleDefaults.defaultColor)
    }
    setFooterImageLink('')
    setHasChanges(false)
    revertSettingsType()
  }

  const loadingEmailTheme =
    configurationMode === configurationTypes.merchant
      ? loadingMerchantEmailTheme || !merchantEmailTheme
      : loadingReturnPortalEmailTheme || !returnPortalEmailTheme

  return {
    colors,
    handleColorChange,
    footerImage,
    handleFooterImageChange,
    footerImageLink,
    handleFooterImageLinkChange,
    textStyles,
    handleFontFamilyChange,
    handleFontSizeChange,
    handleBoldToggle,
    handleItalicToggle,
    handleTextColorChange,
    linkColor,
    handleLinkColorChange,
    handleOnSave,
    savingEmailTheme,
    revertChanges,
    loadingEmailTheme,
    hasChanges,
  }
}

export default useThemesConfiguration
